import React, { useState, useEffect, useRef, useCallback } from "react";
import { SessionTheme, getHud } from "../../config/sessionThemes";
import { MusicPanel } from "./MusicPanel";
import { sessionService, SessionParticipant } from "../../services/sessionService";
import { useWebSocketContext } from "../../contexts/WebSocketContext";

// ── Types ────────────────────────────────────────────────────────────────────

export type PanelId =
  | "world"
  | "notes"
  | "participants"
  | "initiative"
  | "character"
  | "dice"
  | "tools"
  | "knowledge"
  | "codex"
  | "chat"
  | "log";

export interface PanelMeta {
  icon: string;
  title: string;
}

// Popup panel dimensions (music only)
const POPUP_HEIGHT = 300;
const MUSIC_WIDTH = 320;

interface HudToolbarProps {
  sessionId: string;
  userId: string;
  isDM: boolean;
  theme: SessionTheme;
  /** Panel IDs shown as toggle buttons, in order */
  panels: { id: PanelId; meta: PanelMeta }[];
  openPanels: Set<PanelId>;
  onTogglePanel: (_id: PanelId) => void;
  // Leave session
  onLeaveSession?: () => void;
}

/**
 * The bottom HUD toolbar.
 *
 * Renders a centered glass-morphism pill that:
 * - Has one icon toggle per floating panel (including chat and session log)
 * - Has a music toggle that opens a popup panel above the pill
 * - Has a leave-session button
 */
export const HudToolbar: React.FC<HudToolbarProps> = ({
  sessionId,
  userId,
  isDM,
  theme,
  panels,
  openPanels,
  onTogglePanel,
  onLeaveSession,
}) => {
  const hud = getHud(theme);
  const { getChatMessages, getSystemNotifications, getDiceRolls, lastMessage } =
    useWebSocketContext();

  const [musicOpen, setMusicOpen] = useState(false);
  const [participants, setParticipants] = useState<SessionParticipant[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [confirmLeave, setConfirmLeave] = useState(false);

  const lastSeenRef = useRef(0);

  // Aggregate messages for unread badge — exclude the current user's own
  // events (echoed back over the socket) so they never count as unread (#134)
  const totalMessages = [
    ...getChatMessages(sessionId),
    ...getSystemNotifications(sessionId),
    ...getDiceRolls(sessionId),
  ].filter((m) => String(m.senderId) !== String(userId)).length;

  useEffect(() => {
    if (!openPanels.has("chat")) {
      const newUnread = totalMessages - lastSeenRef.current;
      if (newUnread > 0) setUnreadCount(newUnread);
    } else {
      // panel is open — mark all as seen
      lastSeenRef.current = totalMessages;
      setUnreadCount(0);
    }
  }, [totalMessages, openPanels]);

  const handleToggleChat = useCallback(() => {
    if (!openPanels.has("chat")) {
      lastSeenRef.current = totalMessages;
      setUnreadCount(0);
    }
    onTogglePanel("chat");
  }, [totalMessages, openPanels, onTogglePanel]);

  // Fetch participants (used by HudToolbar for user-joined/left refresh)
  const fetchParticipants = useCallback(async () => {
    try {
      const res = await sessionService.getSessionParticipants(sessionId);
      setParticipants(res.participants);
    } catch {
      // silent
    }
  }, [sessionId]);

  // keep participants for any toolbar-level logic; SessionLayout also maintains them
  useEffect(() => {
    fetchParticipants();
  }, [fetchParticipants]);

  useEffect(() => {
    if (!lastMessage) return;
    if (
      (lastMessage.type === "user_joined" || lastMessage.type === "user_left") &&
      String(lastMessage.sessionId) === String(sessionId)
    ) {
      setTimeout(fetchParticipants, 500);
    }
  }, [lastMessage, sessionId, fetchParticipants]);

  // silence unused var lint — participants kept for potential future toolbar badge
  void participants;

  // ── Styles ───────────────────────────────────────────────────────────────

  const pillStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 3,
    background: hud.toolbarBg,
    backdropFilter: "blur(24px)",
    WebkitBackdropFilter: "blur(24px)",
    border: `1px solid ${hud.toolbarBorder}`,
    borderRadius: 40,
    padding: "6px 12px",
    boxShadow: `0 8px 40px rgba(0,0,0,.6), 0 0 0 1px ${hud.snapZoneBorder}`,
  };

  const tbBtn = (on: boolean): React.CSSProperties => ({
    width: 42,
    height: 42,
    borderRadius: "50%",
    border: "none",
    background: on ? hud.toolbarButtonOn : "transparent",
    color: on ? theme.colors.accent : "rgba(255,255,255,.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 19,
    cursor: "pointer",
    boxShadow: on ? `0 0 14px ${hud.toolbarButtonOnGlow}` : "none",
    transition: "all .15s",
    position: "relative",
    flexShrink: 0,
  });

  const sepStyle: React.CSSProperties = {
    width: 1,
    height: 26,
    background: `${hud.toolbarBorder}`,
    margin: "0 3px",
    flexShrink: 0,
  };

  const popupPanelStyle = (width: number): React.CSSProperties => ({
    width,
    height: POPUP_HEIGHT,
    background: hud.panelGlass,
    backdropFilter: "blur(24px)",
    WebkitBackdropFilter: "blur(24px)",
    border: `1px solid ${hud.panelBorderGlass}`,
    borderRadius: "10px 10px 0 0",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    flexShrink: 0,
  });

  const badgeLabel = unreadCount > 99 ? "99+" : String(unreadCount);

  return (
    <div
      data-testid="chat-bottom-bar"
      role="region"
      aria-label="Session toolbar"
      style={{
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        pointerEvents: "none",
        zIndex: 100,
      }}
    >
      {/* ── Music popup (only remaining popup) ────────────────────────────── */}
      {musicOpen && (
        <div data-testid="music-popup-panel" style={{ pointerEvents: "auto" }}>
          <div style={popupPanelStyle(MUSIC_WIDTH)}>
            <MusicPanel sessionId={sessionId} isDM={isDM} theme={theme.id} className="h-full" />
          </div>
        </div>
      )}

      {/* ── Toolbar pill ────────────────────────────────────────────────────── */}
      <div style={{ pointerEvents: "auto", marginBottom: 16, display: "flex" }}>
        <div style={pillStyle}>
          {/* Floating panel toggle buttons */}
          {panels.map(({ id, meta }) =>
            id === "chat" ? (
              <button
                key="chat"
                data-testid="chat-bar-toggle"
                data-toolbar-id="chat"
                onClick={handleToggleChat}
                style={tbBtn(openPanels.has("chat"))}
                title="The Hearth"
                aria-pressed={openPanels.has("chat")}
                aria-label="Toggle chat"
              >
                💬
                {unreadCount > 0 && (
                  <span
                    data-testid="chat-unread-badge"
                    aria-label={`${unreadCount} unread messages`}
                    style={{
                      position: "absolute",
                      top: 2,
                      right: 2,
                      width: 16,
                      height: 16,
                      borderRadius: "50%",
                      background: "#ef4444",
                      color: "#fff",
                      fontSize: 8,
                      fontWeight: 700,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "2px solid rgba(8,11,20,.9)",
                    }}
                  >
                    {badgeLabel}
                  </span>
                )}
              </button>
            ) : (
              <button
                key={id}
                data-toolbar-id={id}
                onClick={() => onTogglePanel(id)}
                style={tbBtn(openPanels.has(id))}
                title={meta.title}
                aria-pressed={openPanels.has(id)}
                aria-label={`Toggle ${meta.title}`}
              >
                {meta.icon}
                {/* Anchor dot — visible when panel is open */}
                {openPanels.has(id) && (
                  <span
                    aria-hidden="true"
                    style={{
                      position: "absolute",
                      top: 2,
                      left: "50%",
                      transform: "translateX(-50%)",
                      width: 5,
                      height: 5,
                      borderRadius: "50%",
                      background: hud.tetherDot,
                      boxShadow: `0 0 6px ${hud.tetherDot}`,
                    }}
                  />
                )}
              </button>
            )
          )}

          <div style={sepStyle} />

          {/*/!* Session log toggle (floating panel) *!/*/}
          {/*<button*/}
          {/*  data-testid="session-log-bar-toggle"*/}
          {/*  data-toolbar-id="log"*/}
          {/*  onClick={() => onTogglePanel("log")}*/}
          {/*  style={tbBtn(openPanels.has("log"))}*/}
          {/*  title="Session Log"*/}
          {/*  aria-pressed={openPanels.has("log")}*/}
          {/*  aria-label="Toggle session log"*/}
          {/*>*/}
          {/*  📜*/}
          {/*</button>*/}

          {/* Music toggle (still a popup) */}
          <button
            data-testid="music-bar-toggle"
            data-toolbar-id="music"
            onClick={() => setMusicOpen((p) => !p)}
            style={tbBtn(musicOpen)}
            title="Music"
            aria-expanded={musicOpen}
            aria-label="Toggle music"
          >
            🎵
          </button>

          <div style={sepStyle} />

          {/* Leave session */}
          {onLeaveSession && !confirmLeave && (
            <button
              data-testid="leave-session-btn"
              onClick={() => setConfirmLeave(true)}
              style={{ ...tbBtn(false), color: "rgba(239,68,68,.7)", fontSize: 17 }}
              title="Leave session"
              aria-label="Leave session"
            >
              🚪
            </button>
          )}
          {onLeaveSession && confirmLeave && (
            <div style={{ display: "flex", alignItems: "center", gap: 4, padding: "0 4px" }}>
              <span style={{ fontSize: 10, color: theme.colors.textPrimary, fontWeight: 600 }}>
                Leave?
              </span>
              <button
                data-testid="leave-session-confirm"
                onClick={() => {
                  setConfirmLeave(false);
                  onLeaveSession();
                }}
                style={{
                  padding: "4px 8px",
                  borderRadius: 6,
                  border: "none",
                  background: theme.colors.error,
                  color: "#fff",
                  fontSize: 10,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
                aria-label="Confirm leave session"
              >
                Yes
              </button>
              <button
                data-testid="leave-session-cancel"
                onClick={() => setConfirmLeave(false)}
                style={{
                  padding: "4px 8px",
                  borderRadius: 6,
                  border: "none",
                  background: "rgba(255,255,255,.1)",
                  color: theme.colors.textPrimary,
                  fontSize: 10,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
                aria-label="Cancel leave session"
              >
                No
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
